/**
 * ABOUT US PAGE — GSAP Animations
 * Monsieur Aure — Cinematic Editorial Motion
 *
 * Dependencies: GSAP 3.12+ & ScrollTrigger
 * All animations are scoped to .about-* selectors.
 * Respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Utilities                                                          */
  /* ------------------------------------------------------------------ */

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function waitForGSAP(callback) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      callback();
    } else {
      setTimeout(function () { waitForGSAP(callback); }, 100);
    }
  }

  /** Split text into <span class="char"> wrappers */
  function splitChars(el) {
    const text = el.textContent;
    el.innerHTML = '';
    for (let i = 0; i < text.length; i++) {
      const span = document.createElement('span');
      span.className = 'char';
      span.textContent = text[i] === ' ' ? '\u00A0' : text[i];
      el.appendChild(span);
    }
    return el.querySelectorAll('.char');
  }

  /** Split text into <span class="word"> wrappers */
  function splitWords(el) {
    const words = el.textContent.trim().split(/\s+/);
    el.innerHTML = '';
    words.forEach(function (word, i) {
      const span = document.createElement('span');
      span.className = 'word';
      span.textContent = word;
      el.appendChild(span);
      if (i < words.length - 1) {
        el.appendChild(document.createTextNode(' '));
      }
    });
    return el.querySelectorAll('.word');
  }

  /** Split text into <span class="line"> wrappers by <br> or sentence */
  function splitLines(el) {
    const html = el.innerHTML;
    const parts = html.split(/<br\s*\/?>/gi);
    el.innerHTML = '';
    parts.forEach(function (part) {
      const span = document.createElement('span');
      span.className = 'line';
      span.innerHTML = part.trim();
      el.appendChild(span);
    });
    return el.querySelectorAll('.line');
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                               */
  /* ------------------------------------------------------------------ */

  function init() {
    gsap.registerPlugin(ScrollTrigger);

    if (prefersReducedMotion) {
      revealAllImmediately();
      return;
    }

    initHero();
    initPhilosophy();
    initTimeline();
    initCraftsmanship();
    initGallery();
    initFounder();
    initClosing();
  }

  /** For prefers-reduced-motion: show everything, no animation */
  function revealAllImmediately() {
    document.querySelectorAll('.char, .word, .line').forEach(function (el) {
      el.style.opacity = '1';
      el.style.clipPath = 'none';
      el.style.transform = 'none';
    });
    document.querySelectorAll('.about-hero__subheading').forEach(function (el) {
      el.style.clipPath = 'none';
    });
    document.querySelectorAll('.about-philosophy__text, .about-timeline__entry, .about-founder__portrait-wrap, .about-founder__name, .about-founder__title, .about-closing__cta, .about-gallery__item').forEach(function (el) {
      el.style.opacity = '1';
      el.style.clipPath = 'none';
      el.style.transform = 'none';
    });
    document.querySelectorAll('.about-hero__rule, .about-philosophy__rule, .about-founder__rule, .about-craftsmanship__rule').forEach(function (el) {
      el.style.transform = 'scaleX(1)';
    });
  }

  /* ------------------------------------------------------------------ */
  /*  1 — Cinematic Hero                                                 */
  /* ------------------------------------------------------------------ */

  function initHero() {
    var heroes = document.querySelectorAll('.about-hero');
    heroes.forEach(function (hero) {
      var bg = hero.querySelector('.about-hero__bg');
      var heading = hero.querySelector('.about-hero__heading');
      var sub = hero.querySelector('.about-hero__subheading');
      var rule = hero.querySelector('.about-hero__rule');
      var speed = parseFloat(hero.dataset.parallaxStrength) || 0.3;

      // Parallax background
      if (bg) {
        gsap.fromTo(bg, { yPercent: -speed * 10 }, {
          yPercent: speed * 10,
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      }

      // Character reveal on heading
      if (heading) {
        var chars = splitChars(heading);
        gsap.to(chars, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.025,
          ease: 'power2.out',
          delay: 0.3,
          scrollTrigger: {
            trigger: hero,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        });
        gsap.set(chars, { opacity: 0, y: 20 });
      }

      // Subheading mask reveal
      if (sub) {
        gsap.to(sub, {
          clipPath: 'inset(0 0 0% 0)',
          duration: 0.8,
          ease: 'power2.out',
          delay: 0.7,
          scrollTrigger: {
            trigger: hero,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        });
      }

      // Rule line
      if (rule) {
        gsap.to(rule, {
          scaleX: 1,
          duration: 0.8,
          ease: 'power2.out',
          delay: 1,
          scrollTrigger: {
            trigger: hero,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  2 — Brand Philosophy                                               */
  /* ------------------------------------------------------------------ */

  function initPhilosophy() {
    var sections = document.querySelectorAll('.about-philosophy');
    sections.forEach(function (section) {
      var headline = section.querySelector('.about-philosophy__headline');
      var text = section.querySelector('.about-philosophy__text');
      var rule = section.querySelector('.about-philosophy__rule');

      if (headline) {
        var words = splitWords(headline);
        gsap.to(words, {
          clipPath: 'inset(0 0 0% 0)',
          duration: 0.6,
          stagger: 0.08,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        });
      }

      if (rule) {
        gsap.to(rule, {
          scaleX: 1,
          duration: 0.8,
          ease: 'power2.out',
          delay: 0.3,
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        });
      }

      if (text) {
        gsap.to(text, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          delay: 0.5,
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        });
        gsap.set(text, { opacity: 0, y: 30 });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  3 — Heritage Timeline                                              */
  /* ------------------------------------------------------------------ */

  function initTimeline() {
    var sections = document.querySelectorAll('.about-timeline');
    sections.forEach(function (section) {
      var line = section.querySelector('.about-timeline__line');
      var entries = section.querySelectorAll('.about-timeline__entry');

      // Growing line — scroll-scrubbed
      if (line) {
        gsap.to(line, {
          scaleY: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: section.querySelector('.about-timeline__entries'),
            start: 'top 80%',
            end: 'bottom 20%',
            scrub: true
          }
        });
      }

      // Staggered entry reveals
      entries.forEach(function (entry, i) {
        var xDir = i % 2 === 0 ? -40 : 40;
        gsap.to(entry, {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: entry,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        });
        gsap.set(entry, { opacity: 0, x: xDir });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  4 — Craftsmanship                                                  */
  /* ------------------------------------------------------------------ */

  function initCraftsmanship() {
    var sections = document.querySelectorAll('.about-craftsmanship');
    sections.forEach(function (section) {
      var primary = section.querySelector('.about-craftsmanship__image-primary');
      var secondary = section.querySelector('.about-craftsmanship__image-secondary');
      var textEls = section.querySelectorAll('.about-craftsmanship__label, .about-craftsmanship__heading, .about-craftsmanship__desc');
      var rule = section.querySelector('.about-craftsmanship__rule');

      // Primary image parallax + scale
      if (primary) {
        var img = primary.querySelector('img, svg');
        if (img) {
          gsap.fromTo(img, { scale: 1.08 }, {
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: primary,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true
            }
          });
        }
      }

      // Secondary image counter-parallax
      if (secondary) {
        gsap.fromTo(secondary, { y: 40 }, {
          y: -40,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      }

      // Text drift in
      textEls.forEach(function (el, i) {
        gsap.from(el, {
          y: 50,
          opacity: 0,
          duration: 0.8,
          delay: i * 0.12,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 65%',
            toggleActions: 'play none none none'
          }
        });
      });

      // Rule
      if (rule) {
        gsap.from(rule, {
          scaleX: 0,
          duration: 0.8,
          ease: 'power2.out',
          delay: 0.3,
          scrollTrigger: {
            trigger: section,
            start: 'top 65%',
            toggleActions: 'play none none none'
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  5 — Atelier Gallery                                                */
  /* ------------------------------------------------------------------ */

  function initGallery() {
    var sections = document.querySelectorAll('.about-gallery');
    sections.forEach(function (section) {
      var items = section.querySelectorAll('.about-gallery__item');
      items.forEach(function (item, i) {
        gsap.to(item, {
          clipPath: 'inset(0 0 0 0)',
          duration: 0.8,
          ease: 'power2.out',
          delay: i * 0.12,
          scrollTrigger: {
            trigger: item,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  6 — Founder Message                                                */
  /* ------------------------------------------------------------------ */

  function initFounder() {
    var sections = document.querySelectorAll('.about-founder');
    sections.forEach(function (section) {
      var portrait = section.querySelector('.about-founder__portrait-wrap');
      var quote = section.querySelector('.about-founder__quote');
      var rule = section.querySelector('.about-founder__rule');
      var name = section.querySelector('.about-founder__name');
      var title = section.querySelector('.about-founder__title');

      // Portrait soft scale
      if (portrait) {
        var img = portrait.querySelector('img, svg');
        gsap.to(portrait, {
          opacity: 1,
          duration: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        });
        if (img) {
          gsap.fromTo(img, { scale: 1.15 }, {
            scale: 1,
            duration: 1.2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 70%',
              toggleActions: 'play none none none'
            }
          });
        }
      }

      // Quote lines reveal
      if (quote) {
        var lines = splitLines(quote);
        lines.forEach(function (line, i) {
          gsap.to(line, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            delay: 0.3 + i * 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 65%',
              toggleActions: 'play none none none'
            }
          });
          gsap.set(line, { opacity: 0, y: 20 });
        });
      }

      // Rule
      if (rule) {
        gsap.to(rule, {
          scaleX: 1,
          duration: 0.8,
          ease: 'power2.out',
          delay: 0.6,
          scrollTrigger: {
            trigger: section,
            start: 'top 65%',
            toggleActions: 'play none none none'
          }
        });
      }

      // Attribution
      [name, title].forEach(function (el, i) {
        if (el) {
          gsap.to(el, {
            opacity: 1,
            duration: 0.6,
            delay: 0.8 + i * 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 65%',
              toggleActions: 'play none none none'
            }
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  7 — Closing Statement                                              */
  /* ------------------------------------------------------------------ */

  function initClosing() {
    var sections = document.querySelectorAll('.about-closing');
    sections.forEach(function (section) {
      var bg = section.querySelector('.about-closing__bg');
      var heading = section.querySelector('.about-closing__heading');
      var cta = section.querySelector('.about-closing__cta');

      // Background parallax
      if (bg) {
        gsap.fromTo(bg, { yPercent: -5 }, {
          yPercent: 5,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      }

      // Character reveal on heading
      if (heading) {
        var chars = splitChars(heading);
        gsap.set(chars, { opacity: 0, y: 15 });
        gsap.to(chars, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.02,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        });
      }

      // CTA fade in
      if (cta) {
        gsap.to(cta, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          delay: 0.5,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        });
        gsap.set(cta, { opacity: 0, y: 15 });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Shopify Theme Editor Support                                       */
  /* ------------------------------------------------------------------ */

  function handleEditorReload() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function () {
        ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
        init();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Boot                                                               */
  /* ------------------------------------------------------------------ */

  function boot() {
    waitForGSAP(function () {
      init();
      handleEditorReload();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
